import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import type {
  ConversationListItem,
  AddMessagePayload,
  ConversationDocument,
  DraftStatus,
  LoadedConversation,
  LoadedConversationSession,
  ChatMessage,
  PendingDraft,
  ToolResult,
  AnalysisType,
  EsignChatSummary,
  MergeManifestEntry,
  WorkspaceSessionSnapshot,
} from '../types';
import { parseEsignSummary } from '../lib/esignChat';
import { withNetworkRetry } from '../lib/networkRetry';

const STUDY_TOOL_NAMES = new Set<string>([
  // StudyToolName (file-based study tools)
  'summarize_document',
  'generate_study_questions',
  'extract_key_terms',
  // AnalysisType (prompt-based analysis — also produce inline text results)
  'summarize',
  'study_questions',
  'key_terms',
  'citation_generator',
  'contract_review',
  'action_items',
  'email_draft',
  'sensitive_data',
]);

function isStudyToolName(value: string): boolean {
  return STUDY_TOOL_NAMES.has(value);
}

const LS_CONVERSATIONS_KEY = 'corner:conversations';
const LS_CONVERSATIONS_SCHEMA_KEY = 'corner:conversations-schema';
const LS_MESSAGES_PREFIX = 'corner:conversation:';
/** Cached conversation list for authenticated users — shown instantly while fresh data loads. */
const LS_AUTH_CONV_CACHE_KEY = 'corner:auth-conv-cache';
const LS_MESSAGES_SUFFIX = ':messages';
const MAX_CONVERSATIONS = 50;
const CONVERSATIONS_SCHEMA_V = 1;
/** Matches backend default; max 100 server-side. */
const DEFAULT_MESSAGES_PAGE_SIZE = 50;

function getApiBase(): string {
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

/** Stored conversation list item in localStorage (lastMessageAt as ISO string). */
interface StoredConversation {
  id: string;
  title: string;
  lastMessageAt: string;
  messageCount: number;
  toolsUsed: string[];
  latestResultFileId?: string;
  latestResultFileName?: string;
  latestResultMimeType?: string;
  archived?: boolean;
  /** Guest trash (distinct from archived folder). */
  trashed?: boolean;
  pinned?: boolean;
  folderId?: string;
  sessionSnapshot?: WorkspaceSessionSnapshot | null;
}

/** Stored message in localStorage. */
interface StoredMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  attachmentName?: string;
  attachments?: Array<{ fileId: string; fileName: string; mimeType: string; sizeBytes: number }>;
  systemKind?: 'esign_entry' | 'multi_files_loaded' | 'pdf_single_loaded';
  toolCall?: {
    toolName: string;
    resultFileId?: string;
    resultFileName?: string;
    resultMimeType?: string;
    resultSizeBytes?: number;
    params?: Record<string, unknown>;
  };
}

function readStoredConversations(): StoredConversation[] {
  const raw = localStorage.getItem(LS_CONVERSATIONS_KEY);
  if (!raw) return [];
  return JSON.parse(raw) as StoredConversation[];
}

function saveConversationsToLS(list: StoredConversation[]): void {
  try {
    const sorted = [...list].sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    );
    localStorage.setItem(LS_CONVERSATIONS_KEY, JSON.stringify(sorted.slice(0, MAX_CONVERSATIONS)));
  } catch {
    console.warn('corner: could not persist conversations to localStorage');
  }
}

/**
 * Older builds used `archived: true` for “moved to trash”. After schema v1, trash uses `trashed`
 * and archive uses `archived` only.
 */
function ensureConversationSchemaV1(list: StoredConversation[]): StoredConversation[] {
  let schema = 0;
  try {
    const m = localStorage.getItem(LS_CONVERSATIONS_SCHEMA_KEY);
    if (m) schema = (JSON.parse(m) as { v?: number }).v ?? 0;
  } catch {
    schema = 0;
  }
  if (schema >= CONVERSATIONS_SCHEMA_V) {
    let dirty = false;
    const normalized = list.map((c) => {
      if (c.trashed === undefined || c.archived === undefined) dirty = true;
      return { ...c, archived: c.archived ?? false, trashed: c.trashed ?? false };
    });
    if (dirty) saveConversationsToLS(normalized);
    return normalized;
  }
  const next = list.map((c) => {
    const legacyTrash = c.archived === true && c.trashed === undefined;
    if (legacyTrash) {
      return { ...c, archived: false, trashed: true };
    }
    return { ...c, archived: c.archived ?? false, trashed: c.trashed ?? false };
  });
  saveConversationsToLS(next);
  try {
    localStorage.setItem(LS_CONVERSATIONS_SCHEMA_KEY, JSON.stringify({ v: CONVERSATIONS_SCHEMA_V }));
  } catch {
    /* ignore */
  }
  return next;
}

function loadConversationsFromLS(): ConversationListItem[] {
  try {
    let parsed = readStoredConversations();
    parsed = ensureConversationSchemaV1(parsed);
    return parsed
      .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime())
      .slice(0, MAX_CONVERSATIONS)
      .map((c) => ({
        id: c.id,
        title: c.title,
        lastMessageAt: new Date(c.lastMessageAt),
        messageCount: c.messageCount,
        toolsUsed: c.toolsUsed ?? [],
        latestResultFileId: c.latestResultFileId,
        latestResultFileName: c.latestResultFileName,
        latestResultMimeType: c.latestResultMimeType,
        sessionSnapshot: c.sessionSnapshot ?? null,
        archived: c.archived ?? false,
        inTrash: Boolean(c.trashed),
        pinned: c.pinned ?? false,
        folderId: c.folderId,
      }));
  } catch {
    return [];
  }
}

function mapApiRow(c: Record<string, unknown>, inTrash: boolean): ConversationListItem {
  return {
    id: String(c._id ?? c.id),
    title: String(c.title ?? 'New Conversation'),
    lastMessageAt: new Date((c.lastMessageAt as string) ?? Date.now()),
    messageCount: Number(c.messageCount ?? 0),
    toolsUsed: Array.isArray(c.toolsUsed) ? (c.toolsUsed as string[]) : [],
    latestResultFileId: c.latestResultFileId as string | undefined,
    latestResultFileName: c.latestResultFileName as string | undefined,
    latestResultMimeType: c.latestResultMimeType as string | undefined,
    sessionSnapshot: (c.sessionSnapshot as WorkspaceSessionSnapshot | null | undefined) ?? null,
    archived: Boolean(c.archived ?? false),
    pinned: Boolean(c.pinned ?? false),
    folderId: c.folderId as string | undefined,
    projectId: c.projectId as string | undefined,
    projectSummary: (c.projectSummary as ConversationListItem['projectSummary']) ?? null,
    historyLocked: Boolean(c.historyLocked ?? false),
    inTrash,
  };
}

function loadMessagesFromLS(conversationId: string): StoredMessage[] {
  try {
    const raw = localStorage.getItem(LS_MESSAGES_PREFIX + conversationId + LS_MESSAGES_SUFFIX);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function saveMessagesToLS(conversationId: string, messages: StoredMessage[]): void {
  try {
    localStorage.setItem(
      LS_MESSAGES_PREFIX + conversationId + LS_MESSAGES_SUFFIX,
      JSON.stringify(messages)
    );
  } catch {
    console.warn('corner: could not persist messages to localStorage');
  }
}

function isMergeManifestEntry(x: unknown): x is MergeManifestEntry {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.fileName === 'string' &&
    typeof o.pageCount === 'number' &&
    typeof o.startPage === 'number' &&
    typeof o.endPage === 'number'
  );
}

function mergeRestoreFromToolParams(
  toolName: string | undefined,
  params: Record<string, unknown> | undefined
): Pick<ToolResult, 'mergeManifest' | 'mergeDurationMs'> | null {
  if (toolName !== 'merge_pdf' || !params) return null;
  const raw = params.mergeManifest;
  if (!Array.isArray(raw)) return null;
  const mergeManifest = raw.filter(isMergeManifestEntry);
  if (mergeManifest.length !== raw.length || mergeManifest.length === 0) return null;
  const mergeDurationMs =
    typeof params.mergeDurationMs === 'number' && params.mergeDurationMs >= 0
      ? params.mergeDurationMs
      : undefined;
  return { mergeManifest, mergeDurationMs };
}

function splitPageCountRestore(
  toolName: string | undefined,
  params: Record<string, unknown> | undefined
): Pick<ToolResult, 'splitPageCount' | 'splitFiles' | 'splitDurationMs'> | null {
  if (toolName !== 'split_pdf' || !params) return null;
  const n = params.splitPageCount;
  if (typeof n !== 'number' || n < 1) return null;
  const result: Pick<ToolResult, 'splitPageCount' | 'splitFiles' | 'splitDurationMs'> = {
    splitPageCount: Math.min(500, Math.floor(n)),
  };
  if (Array.isArray(params.splitFiles) && params.splitFiles.length > 0) {
    result.splitFiles = params.splitFiles as ToolResult['splitFiles'];
  }
  if (typeof params.splitDurationMs === 'number' && params.splitDurationMs >= 0) {
    result.splitDurationMs = params.splitDurationMs;
  }
  return result;
}

function ocrMetaRestore(
  toolName: string | undefined,
  params: Record<string, unknown> | undefined
): Pick<ToolResult, 'ocrMeta' | 'textContent'> | null {
  if (toolName !== 'ocr' || !params) return null;
  const meta = params.ocrMeta;
  if (!meta || typeof meta !== 'object') return null;
  const { confidence, durationMs } = meta as Record<string, unknown>;
  if (typeof confidence !== 'number' || typeof durationMs !== 'number') return null;
  const result: Pick<ToolResult, 'ocrMeta' | 'textContent'> = {
    ocrMeta: { confidence, durationMs },
  };
  if (typeof params.ocrTextContent === 'string' && params.ocrTextContent.length > 0) {
    result.textContent = params.ocrTextContent;
  }
  return result;
}

function qrMetaRestore(
  toolName: string | undefined,
  params: Record<string, unknown> | undefined
): Pick<ToolResult, 'qrMeta'> | null {
  if (toolName !== 'generate_qr' || !params) return null;
  const meta = params.qrMeta;
  if (!meta || typeof meta !== 'object') return null;
  const { payload, sizePx, format, svgContent } = meta as Record<string, unknown>;
  if (typeof payload !== 'string' || typeof sizePx !== 'number' || typeof format !== 'string') return null;
  return {
    qrMeta: {
      payload,
      sizePx,
      format: format as 'png' | 'svg',
      ...(typeof svgContent === 'string' ? { svgContent } : {}),
    },
  };
}

function watermarkMetaRestore(
  toolName: string | undefined,
  params: Record<string, unknown> | undefined
): Pick<ToolResult, 'watermarkMeta'> | null {
  if (toolName !== 'add_watermark_pdf' || !params) return null;
  const meta = params.watermarkMeta;
  if (!meta || typeof meta !== 'object') return null;
  const { text, pageCount } = meta as Record<string, unknown>;
  if (typeof text !== 'string' || typeof pageCount !== 'number') return null;
  return { watermarkMeta: { text, pageCount } };
}

function pageNumbersMetaRestore(
  toolName: string | undefined,
  params: Record<string, unknown> | undefined
): Pick<ToolResult, 'pageNumbersMeta'> | null {
  if (toolName !== 'add_page_numbers' || !params) return null;
  const meta = params.pageNumbersMeta;
  if (!meta || typeof meta !== 'object') return null;
  const { position, pageCount } = meta as Record<string, unknown>;
  if (typeof position !== 'string' || typeof pageCount !== 'number') return null;
  return { pageNumbersMeta: { position, pageCount } };
}

function attachmentNameFromAttachments(
  attachments: Array<{ fileId: string; fileName: string; mimeType: string; sizeBytes: number }> | undefined
): string | undefined {
  if (!attachments || attachments.length === 0) return undefined;
  if (attachments.length === 1) return attachments[0]?.fileName;
  return `${attachments.length} files`;
}

function storedToChatMessage(m: StoredMessage, baseUrl: string): ChatMessage {
  const role = m.role === 'assistant' ? 'corner' : m.role;
  const attachments = m.attachments ?? [];
  const attachmentName = m.attachmentName ?? attachmentNameFromAttachments(attachments);

  // Analysis messages are stored with toolName '__analysis__' and no file result
  if (m.toolCall?.toolName === '__analysis__') {
    const p = m.toolCall.params ?? {};
    return {
      id: m.id,
      role,
      content: m.content,
      timestamp: m.timestamp,
      ...(attachments.length > 0 ? { attachments } : {}),
      ...(attachmentName ? { attachmentName } : {}),
      type: 'analysis' as const,
      ...(p.analysisType ? { analysisType: p.analysisType as AnalysisType } : {}),
      exportable: Boolean(p.exportable ?? false),
    };
  }

  const result: ToolResult | undefined =
    m.toolCall?.resultFileId && m.toolCall?.resultFileName && m.toolCall?.resultMimeType
      ? {
          fileId: m.toolCall.resultFileId,
          downloadUrl: `${baseUrl}/api/file/${m.toolCall.resultFileId}`,
          fileName: m.toolCall.resultFileName,
          mimeType: m.toolCall.resultMimeType,
          sizeBytes: m.toolCall.resultSizeBytes ?? 0,
          // Only restore studyTool for actual study tools so Export button shows correctly
          ...(m.toolCall.toolName && isStudyToolName(m.toolCall.toolName)
            ? { studyTool: m.toolCall.toolName as ToolResult['studyTool'] }
            : {}),
        }
      : undefined;
  const tcParams = m.toolCall?.params as Record<string, unknown> | undefined;
  let esignSummary: EsignChatSummary | undefined;
  if (m.toolCall?.toolName === 'esign' && tcParams?.esignSummary) {
    esignSummary = parseEsignSummary(tcParams.esignSummary);
  }

  const mergeExtra = mergeRestoreFromToolParams(m.toolCall?.toolName, tcParams);
  const splitExtra = splitPageCountRestore(m.toolCall?.toolName, tcParams);
  const ocrExtra = ocrMetaRestore(m.toolCall?.toolName, tcParams);
  const qrExtra = qrMetaRestore(m.toolCall?.toolName, tcParams);
  const wmExtra = watermarkMetaRestore(m.toolCall?.toolName, tcParams);
  const pnExtra = pageNumbersMetaRestore(m.toolCall?.toolName, tcParams);
  const resultWithExtras =
    result && (mergeExtra || splitExtra || ocrExtra || qrExtra || wmExtra || pnExtra)
      ? ({ ...result, ...mergeExtra, ...splitExtra, ...ocrExtra, ...qrExtra, ...wmExtra, ...pnExtra } as ToolResult)
      : result;

  return {
    id: m.id,
    role,
    content: m.content,
    timestamp: m.timestamp,
    ...(attachments.length > 0 ? { attachments } : {}),
    ...(attachmentName && { attachmentName }),
    ...(m.systemKind && { systemKind: m.systemKind }),
    ...(esignSummary && { esignSummary }),
    ...(resultWithExtras && { result: resultWithExtras }),
    // Restore toolName so FollowUpActions and study tool detection work
    ...(m.toolCall?.toolName && { toolName: m.toolCall.toolName }),
    ...(esignSummary && m.toolCall?.toolName === 'esign' && { hideInlineResultCard: true }),
    ...(mergeExtra && m.toolCall?.toolName === 'merge_pdf' && { hideInlineResultCard: true }),
    ...(splitExtra && m.toolCall?.toolName === 'split_pdf' && { hideInlineResultCard: true }),
    ...(wmExtra && m.toolCall?.toolName === 'add_watermark_pdf' && { hideInlineResultCard: true }),
    ...(pnExtra && m.toolCall?.toolName === 'add_page_numbers' && { hideInlineResultCard: true }),
  };
}

function buildLatestResultFromMessages(
  messages: StoredMessage[],
  baseUrl: string
): ToolResult | null {
  const lastWithResult = [...messages].reverse().find((m) => m.toolCall?.resultFileId);
  if (!lastWithResult?.toolCall?.resultFileName || !lastWithResult.toolCall?.resultMimeType)
    return null;
  return {
    fileId: lastWithResult.toolCall.resultFileId!,
    downloadUrl: `${baseUrl}/api/file/${lastWithResult.toolCall.resultFileId}`,
    fileName: lastWithResult.toolCall.resultFileName,
    mimeType: lastWithResult.toolCall.resultMimeType,
    sizeBytes: lastWithResult.toolCall.resultSizeBytes ?? 0,
  };
}

/** Map API role to frontend: assistant -> corner */
function apiRoleToFrontend(role: string): 'user' | 'corner' | 'system' {
  if (role === 'assistant') return 'corner';
  if (role === 'user' || role === 'system') return role;
  return 'corner';
}

function mapRawApiMessagesToChat(
  rawMessages: Array<Record<string, unknown>>,
  baseUrl: string
): ChatMessage[] {
  return rawMessages.map((m: Record<string, unknown>) => {
    const tc = m.toolCall as Record<string, unknown> | undefined;
    const attachmentsRaw = Array.isArray(m.attachments)
      ? (m.attachments as Array<Record<string, unknown>>)
      : [];
    const attachments = attachmentsRaw
      .filter((attachment) => typeof attachment.fileId === 'string' && typeof attachment.fileName === 'string')
      .map((attachment) => ({
        fileId: String(attachment.fileId),
        fileName: String(attachment.fileName),
        mimeType: String(attachment.mimeType ?? 'application/octet-stream'),
        sizeBytes: Number(attachment.sizeBytes ?? 0),
      }));
    const attachmentName = attachmentNameFromAttachments(attachments);
    const baseMsg = {
      id: String(m._id ?? m.id ?? crypto.randomUUID()),
      role: apiRoleToFrontend(String(m.role ?? 'assistant')),
      content: String(m.content ?? ''),
      timestamp: m.createdAt ? new Date(m.createdAt as string).getTime() : Date.now(),
      ...(attachments.length > 0 ? { attachments } : {}),
      ...(attachmentName ? { attachmentName } : {}),
    };

    if (tc?.toolName === '__analysis__') {
      const p = (tc.params ?? {}) as Record<string, unknown>;
      return {
        ...baseMsg,
        type: 'analysis' as const,
        ...(p.analysisType ? { analysisType: p.analysisType as AnalysisType } : {}),
        exportable: Boolean(p.exportable ?? false),
      };
    }

    const p = (tc?.params ?? {}) as Record<string, unknown>;
    const toolNameStr = typeof tc?.toolName === 'string' ? tc.toolName : '';
    const draftHtmlParam =
      typeof p.draftHtml === 'string' && p.draftHtml.trim() ? p.draftHtml : undefined;
    const draftTitleParam =
      typeof p.draftTitle === 'string' && p.draftTitle.trim() ? p.draftTitle : undefined;
    const isDraftTool =
      toolNameStr === 'generate_document' || toolNameStr === 'document_draft';
    const draftFields =
      draftHtmlParam && isDraftTool
        ? {
            draftHtml: draftHtmlParam,
            ...(draftTitleParam ? { draftTitle: draftTitleParam } : {}),
          }
        : {};

    const result: ToolResult | undefined =
      tc?.resultFileId && tc?.resultFileName && tc?.resultMimeType
        ? {
            fileId: String(tc.resultFileId),
            downloadUrl: `${baseUrl}/api/file/${tc.resultFileId}`,
            fileName: String(tc.resultFileName),
            mimeType: String(tc.resultMimeType),
            sizeBytes: Number(tc.resultSizeBytes ?? 0),
            ...(typeof tc.toolName === 'string' && isStudyToolName(tc.toolName)
              ? { studyTool: tc.toolName as ToolResult['studyTool'] }
              : {}),
            ...draftFields,
          }
        : draftHtmlParam && isDraftTool
          ? {
              fileId: `draft-msg-${baseMsg.id}`,
              downloadUrl: '',
              fileName: draftTitleParam
                ? `${draftTitleParam.replace(/\.(docx|doc)$/i, '')}.docx`
                : 'Document.docx',
              mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              sizeBytes: 0,
              ...draftFields,
            }
          : undefined;
    const esignSummary =
      tc?.toolName === 'esign' && p.esignSummary
        ? parseEsignSummary(p.esignSummary)
        : undefined;
    const mergeExtra = mergeRestoreFromToolParams(
      tc?.toolName ? String(tc.toolName) : undefined,
      p
    );
    const splitExtra = splitPageCountRestore(tc?.toolName ? String(tc.toolName) : undefined, p);
    const ocrExtra = ocrMetaRestore(tc?.toolName ? String(tc.toolName) : undefined, p);
    const qrExtra = qrMetaRestore(tc?.toolName ? String(tc.toolName) : undefined, p);
    const wmExtra = watermarkMetaRestore(tc?.toolName ? String(tc.toolName) : undefined, p);
    const pnExtra = pageNumbersMetaRestore(tc?.toolName ? String(tc.toolName) : undefined, p);
    const resultWithExtras =
      result && (mergeExtra || splitExtra || ocrExtra || qrExtra || wmExtra || pnExtra)
        ? ({ ...result, ...mergeExtra, ...splitExtra, ...ocrExtra, ...qrExtra, ...wmExtra, ...pnExtra } as ToolResult)
        : result;
    return {
      ...baseMsg,
      ...(resultWithExtras && { result: resultWithExtras }),
      ...(tc?.toolName ? { toolName: String(tc.toolName) } : {}),
      ...(esignSummary && { esignSummary, hideInlineResultCard: true }),
      ...(mergeExtra && tc?.toolName === 'merge_pdf' && { hideInlineResultCard: true }),
      ...(splitExtra && tc?.toolName === 'split_pdf' && { hideInlineResultCard: true }),
      ...(wmExtra && tc?.toolName === 'add_watermark_pdf' && { hideInlineResultCard: true }),
      ...(pnExtra && tc?.toolName === 'add_page_numbers' && { hideInlineResultCard: true }),
    };
  });
}

function latestResultFromRawMessages(
  rawMessages: Array<Record<string, unknown>>,
  baseUrl: string
): ToolResult | null {
  const lastWithResult = [...rawMessages].reverse().find((m: Record<string, unknown>) => {
    const tc = m.toolCall as Record<string, unknown> | undefined;
    return tc?.resultFileId && tc?.resultFileName && tc?.resultMimeType;
  });
  if (!lastWithResult) return null;
  const tc = lastWithResult.toolCall as Record<string, unknown>;
  return {
    fileId: String(tc.resultFileId),
    downloadUrl: `${baseUrl}/api/file/${tc.resultFileId}`,
    fileName: String(tc.resultFileName),
    mimeType: String(tc.resultMimeType),
    sizeBytes: Number(tc.resultSizeBytes ?? 0),
  };
}

export interface UseConversationsOptions {
  isSignedIn: boolean;
  getToken?: () => string | null;
  /** When true, auth has finished loading and getToken() is ready. Triggers list refetch. */
  authReady?: boolean;
}

export function useConversations(options: UseConversationsOptions) {
  const { isSignedIn, getToken, authReady } = options;
  const [list, setList] = useState<ConversationListItem[]>(() => {
    if (!isSignedIn) return loadConversationsFromLS();
    // Show cached list instantly while fresh data loads in the background
    try {
      const raw = localStorage.getItem(LS_AUTH_CONV_CACHE_KEY);
      if (raw) return JSON.parse(raw) as ConversationListItem[];
    } catch { /* ignore */ }
    return [];
  });
  const [listLoading, setListLoading] = useState(false);
  const baseUrl = getApiBase();

  const refreshList = useCallback(async () => {
    if (!isSignedIn || !getToken?.()) {
      setList(loadConversationsFromLS());
      return;
    }
    setListLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [mainRes, trashRes] = await Promise.all([
        axios.get<{ conversations: Array<Record<string, unknown>> }>('/api/conversations', { headers }),
        axios.get<{ conversations: Array<Record<string, unknown>> }>('/api/conversations/trash', { headers }).catch(() => ({
          data: { conversations: [] as Array<Record<string, unknown>> },
        })),
      ]);
      const main = (mainRes.data.conversations ?? []).map((c) => mapApiRow(c, false));
      const trash = (trashRes.data.conversations ?? []).map((c) => mapApiRow(c, true));
      const merged = [...main, ...trash];
      setList(merged);
      // Persist for instant display on next login
      try { localStorage.setItem(LS_AUTH_CONV_CACHE_KEY, JSON.stringify(merged)); } catch { /* quota */ }
    } catch {
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => {
    if (isSignedIn && getToken?.()) {
      refreshList();
    } else if (!authReady) {
      // Auth still loading — show cached list, will refetch when authReady flips
    } else {
      setList(loadConversationsFromLS());
    }
  }, [isSignedIn, getToken, refreshList, authReady]);

  const createConversation = useCallback(
    async (title?: string): Promise<string> => {
      const safeTitle = (title?.trim() || 'New Conversation').slice(0, 200);
      if (!isSignedIn || !getToken?.()) {
        const id = crypto.randomUUID();
        const now = new Date().toISOString();
        const newItem: StoredConversation = {
          id,
          title: safeTitle,
          lastMessageAt: now,
          messageCount: 0,
          toolsUsed: [],
          archived: false,
          trashed: false,
        };
        const existing = ensureConversationSchemaV1(readStoredConversations());
        saveConversationsToLS([newItem, ...existing]);
        setList(loadConversationsFromLS());
        return id;
      }
      const token = getToken();
      const { data } = await axios.post<{ _id: string }>(
        '/api/conversations',
        { title: safeTitle },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await refreshList();
      return String(data._id);
    },
    [isSignedIn, getToken, refreshList]
  );

  const getMessages = useCallback(
    async (
      conversationId: string,
      opts?: { limit?: number; before?: string }
    ): Promise<LoadedConversation> => {
      if (!isSignedIn || !getToken?.()) {
        const stored = loadMessagesFromLS(conversationId);
        const messages = stored.map((m) => storedToChatMessage(m, baseUrl));
        const latestResult = buildLatestResultFromMessages(stored, baseUrl);
        return { messages, latestResult, hasMore: false };
      }
      const token = getToken();
      const limit = opts?.limit ?? DEFAULT_MESSAGES_PAGE_SIZE;
      const params: Record<string, string> = { limit: String(limit) };
      if (opts?.before) params.before = opts.before;

      // Boot-time hydration runs through this path. Retry transient network
      // failures (Wi-Fi flap on resume from sleep, ERR_NETWORK_CHANGED) so the
      // user isn't dropped on an empty UI for a single dropped packet.
      const { data } = await withNetworkRetry(
        () => axios.get<{
          messages: Array<Record<string, unknown>>;
          hasMore?: boolean;
        }>(`/api/conversations/${conversationId}/messages`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params,
        }),
        { maxRetries: 3, initialDelayMs: 500 },
      );
      const rawMessages = data.messages ?? [];
      const messages = mapRawApiMessagesToChat(rawMessages, baseUrl);
      const latestResult = latestResultFromRawMessages(rawMessages, baseUrl);
      return { messages, latestResult, hasMore: Boolean(data.hasMore) };
    },
    [isSignedIn, getToken, baseUrl]
  );

  const getSession = useCallback(
    async (
      conversationId: string,
      opts?: { limit?: number; before?: string; nocache?: boolean }
    ): Promise<LoadedConversationSession> => {
      if (!isSignedIn || !getToken?.()) {
        const messagesPayload = await getMessages(conversationId, {
          limit: opts?.limit,
          before: opts?.before,
        });
        const conversation =
          loadConversationsFromLS().find((item) => item.id === conversationId) ??
          {
            id: conversationId,
            title: 'Conversation',
            lastMessageAt: new Date(),
            messageCount: messagesPayload.messages.length,
            toolsUsed: [],
            sessionSnapshot: null,
          };
        return {
          ...messagesPayload,
          conversation,
          sessionSnapshot: conversation.sessionSnapshot ?? null,
        };
      }

      const token = getToken();
      const limit = opts?.limit ?? DEFAULT_MESSAGES_PAGE_SIZE;
      const params: Record<string, string> = { limit: String(limit) };
      if (opts?.before) params.before = opts.before;
      if (opts?.nocache) params.nocache = '1';

      // Same retry policy as getMessages — this is the primary boot-hydration
      // path. A single dropped packet here strands the workspace on the empty
      // state with only a 5s toast for feedback, which is the "stuck on the
      // new flow screen" symptom.
      const { data } = await withNetworkRetry(
        () => axios.get<{
          conversation: Record<string, unknown>;
          sessionSnapshot: WorkspaceSessionSnapshot | null;
          messages: Array<Record<string, unknown>>;
          hasMore?: boolean;
          latestResult?: ToolResult | null;
          documents?: ConversationDocument[];
          draftStatus?: DraftStatus;
          pendingDraft?: PendingDraft | null;
        }>(`/api/conversations/${conversationId}/session`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params,
        }),
        { maxRetries: 3, initialDelayMs: 500 },
      );

      return {
        conversation: mapApiRow(
          {
            ...(data.conversation ?? {}),
            _id: data.conversation?.id ?? conversationId,
            sessionSnapshot: data.sessionSnapshot,
          },
          false,
        ),
        sessionSnapshot: data.sessionSnapshot ?? null,
        messages: mapRawApiMessagesToChat(data.messages ?? [], baseUrl),
        latestResult: data.latestResult ?? latestResultFromRawMessages(data.messages ?? [], baseUrl),
        hasMore: Boolean(data.hasMore),
        documents: data.documents ?? [],
        draftStatus: data.draftStatus ?? 'idle',
        pendingDraft: data.pendingDraft ?? null,
      };
    },
    [baseUrl, getMessages, getToken, isSignedIn],
  );

  const addMessage = useCallback(
    async (
      conversationId: string,
      payload: AddMessagePayload
    ): Promise<void> => {
      if (!isSignedIn || !getToken?.()) {
        const stored = loadMessagesFromLS(conversationId);
        const newMsg: StoredMessage = {
          id: crypto.randomUUID(),
          role: payload.role === 'corner' ? 'assistant' : payload.role,
          content: payload.content,
          timestamp: Date.now(),
          attachmentName: attachmentNameFromAttachments(payload.attachments),
          attachments: payload.attachments,
          toolCall: payload.toolCall,
        };
        const next = [...stored, newMsg];
        saveMessagesToLS(conversationId, next);

        const conversations = ensureConversationSchemaV1(
          readStoredConversations()
        );
        const conv = conversations.find((c) => c.id === conversationId);
        if (conv) {
          conv.lastMessageAt = new Date().toISOString();
          conv.messageCount = next.length;
          if (payload.toolCall?.resultFileId) {
            conv.latestResultFileId = payload.toolCall.resultFileId;
            conv.latestResultFileName = payload.toolCall.resultFileName;
            conv.latestResultMimeType = payload.toolCall.resultMimeType;
          }
          if (payload.toolCall?.toolName && !conv.toolsUsed.includes(payload.toolCall.toolName)) {
            conv.toolsUsed = [...conv.toolsUsed, payload.toolCall.toolName];
          }
          saveConversationsToLS(conversations);
          setList(loadConversationsFromLS());
        }
        return;
      }
      const token = getToken();
      const { data: savedMsg } = await axios.post<Record<string, unknown>>(
        `/api/conversations/${conversationId}/messages`,
        {
          role: payload.role === 'corner' || payload.role === 'assistant' ? 'assistant' : payload.role,
          content: payload.content,
          attachments: payload.attachments ?? [],
          toolCall: payload.toolCall,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      // Optimistic update: patch conversation metadata in state instead of refetching the full list
      setList((prev) =>
        prev.map((conv) => {
          if (conv.id !== conversationId) return conv;
          const tc = payload.toolCall;
          const toolsUsed =
            tc?.toolName && !conv.toolsUsed.includes(tc.toolName)
              ? [...conv.toolsUsed, tc.toolName]
              : conv.toolsUsed;
          return {
            ...conv,
            lastMessageAt: savedMsg.createdAt
              ? new Date(savedMsg.createdAt as string)
              : new Date(),
            messageCount: conv.messageCount + 1,
            toolsUsed,
            ...(tc?.resultFileId
              ? {
                  latestResultFileId:   tc.resultFileId,
                  latestResultFileName: tc.resultFileName,
                  latestResultMimeType: tc.resultMimeType,
                }
              : {}),
          };
        })
      );
    },
    [isSignedIn, getToken]
  );

  const deleteConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      if (!isSignedIn || !getToken?.()) {
        const raw = localStorage.getItem(LS_CONVERSATIONS_KEY);
        const conversations: StoredConversation[] = raw ? JSON.parse(raw) : [];
        const next = conversations.map((c) =>
          c.id === conversationId ? { ...c, trashed: true, archived: false } : c
        );
        saveConversationsToLS(next);
        setList(loadConversationsFromLS());
        return;
      }
      const token = getToken();
      await axios.delete(`/api/conversations/${conversationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      await refreshList();
    },
    [isSignedIn, getToken, refreshList]
  );

  const permanentlyDeleteConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      if (!isSignedIn || !getToken?.()) {
        const raw = localStorage.getItem(LS_CONVERSATIONS_KEY);
        const conversations: StoredConversation[] = raw ? JSON.parse(raw) : [];
        const next = conversations.filter((c) => c.id !== conversationId);
        saveConversationsToLS(next);
        localStorage.removeItem(LS_MESSAGES_PREFIX + conversationId + LS_MESSAGES_SUFFIX);
        setList(loadConversationsFromLS());
        return;
      }
      const token = getToken();
      await axios.delete(`/api/conversations/${conversationId}/permanent`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      await refreshList();
    },
    [isSignedIn, getToken, refreshList]
  );

  const restoreFromTrash = useCallback(
    async (conversationId: string): Promise<void> => {
      if (!isSignedIn || !getToken?.()) {
        const raw = localStorage.getItem(LS_CONVERSATIONS_KEY);
        const conversations: StoredConversation[] = raw ? JSON.parse(raw) : [];
        const next = conversations.map((c) =>
          c.id === conversationId ? { ...c, trashed: false } : c
        );
        saveConversationsToLS(next);
        setList(loadConversationsFromLS());
        return;
      }
      const token = getToken();
      await axios.post(
        `/api/conversations/${conversationId}/restore`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await refreshList();
    },
    [isSignedIn, getToken, refreshList]
  );

  const emptyTrash = useCallback(async (): Promise<void> => {
    if (!isSignedIn || !getToken?.()) {
      const raw = localStorage.getItem(LS_CONVERSATIONS_KEY);
      const conversations: StoredConversation[] = raw ? JSON.parse(raw) : [];
      const keep = conversations.filter((c) => !c.trashed);
      const removed = conversations.filter((c) => c.trashed);
      removed.forEach((c) => {
        try {
          localStorage.removeItem(LS_MESSAGES_PREFIX + c.id + LS_MESSAGES_SUFFIX);
        } catch {
          /* ignore */
        }
      });
      saveConversationsToLS(keep);
      setList(loadConversationsFromLS());
      return;
    }
    const token = getToken();
    await axios.delete('/api/conversations/trash', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    await refreshList();
  }, [isSignedIn, getToken, refreshList]);

  const updateConversation = useCallback(
    async (
      conversationId: string,
      patch: { title?: string; archived?: boolean; pinned?: boolean; folderId?: string | null }
    ): Promise<void> => {
      if (!isSignedIn || !getToken?.()) {
        const conversations = ensureConversationSchemaV1(readStoredConversations());
        const conv = conversations.find((c) => c.id === conversationId);
        if (conv) {
          if (patch.title !== undefined) conv.title = patch.title.trim().slice(0, 200) || 'New Conversation';
          if (patch.archived !== undefined) {
            conv.archived = patch.archived;
            if (patch.archived) conv.trashed = false;
          }
          if (patch.pinned !== undefined) conv.pinned = patch.pinned;
          if (patch.folderId !== undefined) {
            if (patch.folderId === null || patch.folderId === '') {
              delete conv.folderId;
            } else {
              conv.folderId = patch.folderId;
            }
          }
          saveConversationsToLS(conversations);
          setList(loadConversationsFromLS());
        }
        return;
      }
      const token = getToken();
      await axios.patch(
        `/api/conversations/${conversationId}`,
        patch,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await refreshList();
    },
    [isSignedIn, getToken, refreshList]
  );

  const clearAll = useCallback(() => {
    if (isSignedIn && getToken?.()) return; // No API for bulk delete; leave list as-is
    try {
      const raw = localStorage.getItem(LS_CONVERSATIONS_KEY);
      const conversations: StoredConversation[] = raw ? JSON.parse(raw) : [];
      conversations.forEach((c) => {
        localStorage.removeItem(LS_MESSAGES_PREFIX + c.id + LS_MESSAGES_SUFFIX);
      });
      localStorage.removeItem(LS_CONVERSATIONS_KEY);
      setList([]);
    } catch {
      setList([]);
    }
  }, [isSignedIn, getToken]);

  const persistSnapshot = useCallback(
    async (conversationId: string, snapshot: Omit<WorkspaceSessionSnapshot, 'conversationId' | 'updatedAt'>): Promise<void> => {
      if (!isSignedIn || !getToken?.()) {
        const conversations = ensureConversationSchemaV1(readStoredConversations());
        const conv = conversations.find((c) => c.id === conversationId);
        if (!conv) return;
        conv.sessionSnapshot = {
          ...snapshot,
          conversationId,
          updatedAt: new Date().toISOString(),
        };
        saveConversationsToLS(conversations);
        setList(loadConversationsFromLS());
        return;
      }

      const token = getToken();
      // Snapshot writes are idempotent — last-write-wins. Wrap in retry so
      // a Wi-Fi flap (ERR_NETWORK_CHANGED) during a save doesn't strand the
      // session in an unsaved state. Permanent errors (4xx, auth) bubble up.
      const res = await withNetworkRetry(
        () => axios.patch<{ snapshot?: WorkspaceSessionSnapshot }>(
          `/api/conversations/${conversationId}/snapshot`,
          { snapshot },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        ),
        { maxRetries: 3, initialDelayMs: 500 },
      );
      const savedSnapshot = res.data.snapshot ?? {
        ...snapshot,
        conversationId,
        updatedAt: new Date().toISOString(),
      };
      setList((prev) =>
        prev.map((conversation) =>
          conversation.id === conversationId
            ? {
                ...conversation,
                projectId: savedSnapshot.projectId ?? conversation.projectId,
                sessionSnapshot: savedSnapshot,
              }
            : conversation,
        ),
      );
    },
    [getToken, isSignedIn],
  );

  /**
   * In-place edit of a draft document body. Used by the rich-text
   * editor to keep the canonical document store fresh as the user
   * types — bypasses the snapshot debounce path entirely so
   * editor changes survive even if the snapshot PATCH races a
   * navigation. Best-effort: a transient network failure is
   * swallowed (the caller's debounce will retry on the next change).
   */
  const updateConversationDocument = useCallback(
    async (
      conversationId: string,
      documentId: string,
      patch: { html?: string; title?: string },
    ): Promise<ConversationDocument | null> => {
      if (!isSignedIn || !getToken?.()) return null;
      const token = getToken();
      try {
        const res = await withNetworkRetry(
          () => axios.patch<{ document: ConversationDocument }>(
            `/api/conversations/${conversationId}/documents/${documentId}`,
            patch,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          ),
          { maxRetries: 2, initialDelayMs: 500 },
        );
        return res.data.document;
      } catch {
        return null;
      }
    },
    [getToken, isSignedIn],
  );

  return {
    list,
    listLoading,
    createConversation,
    getMessages,
    getSession,
    addMessage,
    deleteConversation,
    permanentlyDeleteConversation,
    restoreFromTrash,
    emptyTrash,
    updateConversation,
    persistSnapshot,
    updateConversationDocument,
    refreshList,
    clearAll,
    getApiBase: () => baseUrl,
  };
}
